"use client";

import React, { useState } from 'react';
// Tree-shaken icon imports for optimal bundle size
import Clock from "lucide-react/dist/esm/icons/clock";
import Flame from "lucide-react/dist/esm/icons/flame";
import Wheat from "lucide-react/dist/esm/icons/wheat";
import Beef from "lucide-react/dist/esm/icons/beef";
import Droplet from "lucide-react/dist/esm/icons/droplet";
import Lightbulb from "lucide-react/dist/esm/icons/lightbulb";
import { Skeleton } from "@/components/ui/skeleton";
import Image from "next/image";
import { COMPONENT_IMAGE_CONFIG, getImageProps, getOptimizedImageUrl } from '@/lib/imageConfig';
import type { Recipe } from "./RecipeCard";
import { RecipeQualitySection } from "./RecipeQualitySection";

export interface RecipeContentProps {
    recipe: Recipe;
    isLoading?: boolean;
    showQualitySection?: boolean;
    showActionButtons?: boolean;
    actionButtons?: React.ReactNode;
}

export const RecipeContent: React.FC<RecipeContentProps> = ({
    recipe,
    isLoading = false,
    showQualitySection = false,
    showActionButtons = false,
    actionButtons
}) => {
    const [imageLoaded, setImageLoaded] = useState<{ [key: string]: boolean }>({});

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-64 w-full" />
                <div className="space-y-4">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Recipe Image - Always reserve space to prevent jumping */}
            <div className="relative h-64">
                {/* Always show placeholder first */}
                <div className="w-full h-64 bg-gradient-to-br from-orange-100/50 to-amber-100/50 rounded-2xl shadow-sm flex items-center justify-center">
                    <div className="text-center text-orange-600/60">
                        <div className="text-4xl mb-2">🍽️</div>
                        <div className="text-sm font-medium">טוען תמונה...</div>
                    </div>
                </div>

                {/* Overlay image when loaded */}
                {recipe.image && (
                    <Image
                        src={getOptimizedImageUrl(recipe.image, COMPONENT_IMAGE_CONFIG.RECIPE_DETAIL.size, COMPONENT_IMAGE_CONFIG.RECIPE_DETAIL.quality)}
                        alt={`תמונה של מתכון: ${recipe.title}`}
                        fill
                        className={`absolute inset-0 object-cover rounded-2xl shadow-lg transition-opacity duration-300 ${imageLoaded[recipe.id || ''] ? 'opacity-100' : 'opacity-0'
                            }`}
                        {...getImageProps(COMPONENT_IMAGE_CONFIG.RECIPE_DETAIL)}
                        onLoad={() => {
                            if (recipe.id) {
                                setImageLoaded(prev => ({ ...prev, [recipe.id!]: true }));
                            }
                        }}
                        onError={() => {
                            if (recipe.id) {
                                setImageLoaded(prev => ({ ...prev, [recipe.id!]: false }));
                            }
                        }}
                    />
                )}
            </div>

            {/* Action Buttons (if provided) */}
            {showActionButtons && actionButtons}

            {/* Dish Origin Context */}
            {recipe?.dish_origin_context_he && recipe.dish_origin_context_he.trim() && (
                <div className="bg-gradient-to-br from-amber-50/60 to-yellow-50/50 rounded-2xl p-5 border border-amber-200/40 shadow-sm">
                    <div className="flex items-start gap-3" dir="rtl">
                        <div className="flex-shrink-0 text-2xl">🌍</div>
                        <div className="flex-1">
                            <h3 className="text-base font-bold text-amber-800 mb-2">רקע תרבותי</h3>
                            <p className="text-gray-800 text-base leading-relaxed" itemProp="recipeOrigin">
                                {recipe.dish_origin_context_he}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Nutritional Info */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-6 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-5 tracking-wide">ערכים תזונתיים (לכל 100 גרם)</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" dir="rtl">
                    {/* Calories */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-orange-50/50 backdrop-blur-sm border border-orange-200/30 hover:bg-orange-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl shadow-sm">
                            <Flame className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-orange-900 leading-none">
                            {recipe?.calories_per_100g != null ? Math.round(Number(recipe.calories_per_100g)) : '—'}
                        </div>
                        <div className="text-xs font-semibold text-orange-700">קלוריות</div>
                    </div>

                    {/* Carbs */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-amber-50/50 backdrop-blur-sm border border-amber-200/30 hover:bg-amber-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl shadow-sm">
                            <Wheat className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-amber-900 leading-none">
                            {recipe?.carbs_per_100g != null ? Math.round(Number(recipe.carbs_per_100g)) : '—'}
                            <span className="text-lg">g</span>
                        </div>
                        <div className="text-xs font-semibold text-amber-700">פחמימות</div>
                    </div>

                    {/* Protein */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-red-50/50 backdrop-blur-sm border border-red-200/30 hover:bg-red-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-red-400 to-red-500 rounded-xl shadow-sm">
                            <Beef className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-red-900 leading-none">
                            {recipe?.protein_per_100g != null ? Math.round(Number(recipe.protein_per_100g)) : '—'}
                            <span className="text-lg">g</span>
                        </div>
                        <div className="text-xs font-semibold text-red-700">חלבון</div>
                    </div>

                    {/* Fat */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-blue-50/50 backdrop-blur-sm border border-blue-200/30 hover:bg-blue-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl shadow-sm">
                            <Droplet className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-blue-900 leading-none">
                            {recipe?.fat_per_100g != null ? Math.round(Number(recipe.fat_per_100g)) : '—'}
                            <span className="text-lg">g</span>
                        </div>
                        <div className="text-xs font-semibold text-blue-700">שומן</div>
                    </div>
                </div>
            </div>

            {/* Quality Section */}
            {showQualitySection && (
                <RecipeQualitySection
                    qualityScore={recipe.recipe_quality_score}
                    qualityExplanation={recipe.quality_explanation_he}
                />
            )}

            {/* Time Info */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-5 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-4 tracking-wide">זמני הכנה</h3>
                <div className="flex items-center justify-center gap-8" dir="rtl">
                    {recipe?.estimated_prep_time && (
                        <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-orange-400/60" />
                            <div className="text-right">
                                <div className="text-sm font-bold text-orange-700">התעסקות</div>
                                <div className="text-xl font-bold text-orange-900">{recipe.estimated_prep_time}</div>
                            </div>
                        </div>
                    )}
                    {recipe?.estimated_prep_time && recipe?.estimated_total_time && (
                        <div className="h-10 w-px bg-orange-300/40"></div>
                    )}
                    {recipe?.estimated_total_time && (
                        <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-orange-400/60" />
                            <div className="text-right">
                                <div className="text-sm font-bold text-orange-700">כולל</div>
                                <div className="text-xl font-bold text-orange-900">{recipe.estimated_total_time}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Ingredients */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-6 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-5 tracking-wide">מצרכים</h3>
                <div className="space-y-2">
                    {(recipe?.ingredients as { name: string; amount: string }[] || []).map((ingredient, index) => (
                        <div key={index} className="flex items-center gap-3 p-4 rounded-xl bg-orange-50/40 backdrop-blur-sm border border-orange-100/30 hover:bg-orange-50/60 transition-colors shadow-sm" dir="rtl">
                            <p className="text-gray-800 text-lg leading-relaxed">
                                <span className="font-bold text-gray-900">{ingredient.name}</span>
                                <span className="text-orange-400/60 mx-3">·</span>
                                <span className="text-gray-700">{ingredient.amount}</span>
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Preparation */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-6 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-5 tracking-wide">אופן ההכנה</h3>
                <div className="space-y-4 text-right" dir="rtl">
                    {(() => {
                        const text = recipe?.preparation || '';
                        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
                        if (lines.length === 0) {
                            return <p className="text-muted-foreground text-center py-8">אין הוראות הכנה</p>;
                        }

                        let stepCounter = 0;

                        return lines.map((line, idx) => {
                            const stepMatch = line.match(/^(\d+)[\.)]\s*(.*)$/);
                            if (stepMatch) {
                                const num = stepMatch[1];
                                const content = stepMatch[2] || '';
                                stepCounter++;
                                return (
                                    <div key={idx} className="flex items-start gap-4 bg-orange-50/40 backdrop-blur-sm border border-orange-100/30 rounded-xl p-4 hover:bg-orange-50/60 transition-colors shadow-sm">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 text-white flex items-center justify-center text-sm font-bold shadow-sm">
                                            {num}
                                        </div>
                                        <div className="flex-1 pt-0.5">
                                            <p className="text-gray-800 leading-relaxed text-lg">{content}</p>
                                        </div>
                                    </div>
                                );
                            }

                            if (line.startsWith('-')) {
                                const content = line.replace(/^-+\s*/, '');
                                return (
                                    <div key={idx} className="flex items-start gap-3.5 bg-gradient-to-l from-amber-50/60 to-yellow-50/40 border-r-4 border-amber-400/70 rounded-xl p-4 shadow-sm backdrop-blur-sm">
                                        <Lightbulb className="flex-shrink-0 w-5 h-5 text-amber-700 mt-0.5" />
                                        <p className="text-gray-900 text-lg leading-relaxed font-medium">{content}</p>
                                    </div>
                                );
                            }

                            // Section header
                            return (
                                <div key={idx} className="pt-4 pb-1">
                                    <h4 className="text-base font-bold text-orange-800 text-center">
                                        {line}
                                    </h4>
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>
        </div>
    );
};
